import React, { useCallback, useMemo, useState, FC } from 'react';
import Image from 'react-bootstrap/Image';
import Form from 'react-bootstrap/Form';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

import type { IAction } from '../../../types/affiliates.types';
import type { IProductCardProps } from '../productCard';
import BindingButton from '../../BindingButton';
import AccountRecovery from '../../AccountRecovery';
import GetHintButton from '../../GetHintButton';
import './style.scss';
import { removeProtocol } from '../../../utils/netUtils';

const ProductItem: FC<IProductCardProps> = ({ data, onActivateButtonClick, onReactivateButtonClick, onGetHint, onGetAccountRecovery }) => {
  const {
    customData,
    cps,
    cpDescription,
    error,
    logId,
    linked,
    hinteable,
    metadata,
    isPreviouslyBound,
    subjectId,
    binding
  } = data;
  const [itemState, setItemState] = useState({
    conditionsAccepted: false,
    isDirty: false,
    isLoading: false
  });
  const terms = useMemo(() => customData?.find(p => p.key && p.key === 'term')?.value, [customData]);
  const redirectURLRegisteredUser = useMemo(() => customData?.find(p => p.key && p.key === 'redirectURLRegisteredUser')?.value, [customData]);
  if (!redirectURLRegisteredUser) {
    console.warn('You must set "redirectURLRegisteredUser" in cloudPay customData to enable button on linked product');
  }

  const suspendAction = useMemo(() => binding?.actions?.find((action: IAction) => action?.action === 'SUSPEND'), [binding]);

  const accomplishAction = useMemo(() => binding?.actions?.find((action: IAction) => action?.action === 'ACCOMPLISH'), [binding]);

  localStorage.setItem(`${cps}-terms`, JSON.stringify(terms));

  const activateButtonClickHandler = useCallback((e: any) => {
    e.preventDefault();

    if (linked && redirectURLRegisteredUser) {
      return window.open(redirectURLRegisteredUser);
    }

    setItemState(prev => ({ ...prev, isLoading: true }));
    
    if (onActivateButtonClick) {
      Promise.resolve(onActivateButtonClick())
        .catch((error) => {
          console.error('Error al activar producto:', error);
        })
        .finally(() => {
          setTimeout(() => {
            setItemState(prev => ({ ...prev, isLoading: false }));
          }, 1000);
        });
    }
  }, [linked, onActivateButtonClick, redirectURLRegisteredUser]);

  const reactivateButtonClickHandler = useCallback((e: any) => {
    e.preventDefault();
    
    setItemState(prev => ({ ...prev, isLoading: true }));
    
    if (onReactivateButtonClick) {
      Promise.resolve(onReactivateButtonClick())
        .catch((error) => {
          console.error('Error al reactivar producto:', error);
        })
        .finally(() => {
          setTimeout(() => {
            setItemState(prev => ({ ...prev, isLoading: false }));
          }, 1000);
        });
    }
  }, [onReactivateButtonClick]);

  const termsCheckboxClickHandler = useCallback((e: any) => {
    setItemState({
      ...itemState,
      conditionsAccepted: e.target.checked
    });
  }, [itemState]);

  const disableButton = useMemo(() => {
    if (error) {
      return true;
    }

    if (linked) {
      return false;
    }

    if (cps === 'prime') {
      return !itemState.conditionsAccepted;
    }

    if (Boolean(terms)) {
      return !itemState.conditionsAccepted;
    }

    return false;
  }, [error, itemState.conditionsAccepted, linked, terms, cps]);

  const showCheck = useMemo(() => {
    return (!linked && (cps === 'prime' || Boolean(terms)) && !error);
  }, [linked, terms, error, cps]);

  const serviceName = useMemo(() => {
    return cpDescription || metadata?.name || cps;
  }, [cpDescription, metadata?.name, cps]);

  const loginUrl = useMemo(() => {
    return data?.loginUrl || '';
  }, [data?.loginUrl]);

  const errorMessage = useMemo(() => {
    if (!error) {
      return '';
    }

    const baseMessage = '* Se ha producido un error en la activación de su producto.';

    if (logId) {
      return `${baseMessage} Código de error: ${error}. Identificador del error: ${logId}`;
    }

    return `${baseMessage} Identificador del error: ${error}`;
  }, [error, logId]);

  return (
    <>
      {binding.status === 'SUSPENDED' && suspendAction ?
        <div className='suspended'>
          {metadata?.image && (
            <div className='suspended-logo'>
              <Image src={metadata.image} alt={serviceName} />
            </div>
          )}
          <p>Tu servicio <strong>{serviceName}</strong> se encuentra suspendido por deuda pendiente, desde el dia {format(suspendAction.updatedAt, 'dd/MM/yyyy')}</p>
          <p>Paga tus cuentas de forma segura desde el portal del pagos</p>
        </div> :
        <div className={`product-item${error ? ' product-item--error' : ''}`}>
          <div className={cps}>
            <div className="content">
              <div className="logo">
                {metadata?.image && <Image src={metadata.image} alt={serviceName} />}
              </div>
              <div className='info'>
    {linked ? (
      <div className="text-activation">
        Te informamos que ya usaste el beneficio de Claro, por favor ingresa a <a href={loginUrl} target="_blank" rel="noopener noreferrer">{removeProtocol(loginUrl)}</a> con el correo y contraseña que creaste en {serviceName} el día {accomplishAction ? format(accomplishAction.updatedAt, 'dd/MM/yyyy') : 'de activación'}.
      </div>
    ) : (
      <BindingButton
        disabled={cps === 'prime' && terms && !itemState.conditionsAccepted ? true : disableButton}
        isLoading={itemState.isLoading}
        onClickHandler={cps === 'disney' && isPreviouslyBound ? reactivateButtonClickHandler : activateButtonClickHandler}
        title={linked ? 'Ingresar' : 'Activar'}
      />
    )}
              </div>
            </div>
            {showCheck &&
              <div className="footer">
                <Form.Check
                  type="checkbox"
                  id="product_condition_of_use"
                >
                  <Form.Check.Input
                    onBlur={() => setItemState({ ...itemState, isDirty: true })}
                    onChange={termsCheckboxClickHandler}
                    type="checkbox"
                    data-testid="term-checkbox"
                    className={itemState.isDirty && !itemState.conditionsAccepted ? 'error' : ''}
                  />
                  <Form.Check.Label className="checkbox_label">
                    {`Acepto los `}
                    <Link
                      to={`/clarocl/${cps}/termsofuse`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {`términos y condiciones`}
                    </Link>
                  </Form.Check.Label>
                </Form.Check>
              </div>
            }
            {subjectId && <div className="footer">Tu ID de suscripción es:<span style={{ marginTop: '4px', marginLeft: '5px' }}>{subjectId}</span></div>}
            <GetHintButton cps={cps} linked={linked} hinteable={hinteable} onGetHint={onGetHint} />
            <AccountRecovery cps={cps} linked={linked} onGetAccountRecovery={onGetAccountRecovery} />
          </div>
          {itemState.isDirty && !itemState.conditionsAccepted &&
            <span className="error-message">Debe aceptar los términos y condiciones</span>
          }
          {error && <span className="error-subscription">{errorMessage}</span>}
        </div>}
    </>
  );
}

export default ProductItem;
